import { Component, effect, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideNavigation } from '@ng-icons/lucide';

import { mockFormOptions } from './debug-form-options/mock-form-options';
import { QuoteFormMappingKey, quoteFormMappingMap } from '../../create-quote/form-config/quote-form.config';
import { QuoteDraftAddress, QuoteDraftStore } from '../../global-store/quote-draft';
import { QuoteFormOptionsStore } from '../../global-store/quote-form-options/quote-form-options.store';
import { UserStore } from '../../global-store/user/user.store';

@Component({
  selector: 'app-debug-scenarios',
  imports: [FormsModule, ReactiveFormsModule, NgIconComponent],
  providers: [provideIcons({ lucideNavigation })],
  template: `
    <div class="space-y-8">
      <!-- Navigation Section -->
      <div>
        <h4 class="text-fs-sm h4 mb-4 flex items-center gap-2 border-b pb-2">
          <ng-icon name="lucideNavigation" size="1em" class=""></ng-icon>
          Navigate To Form
        </h4>
        <div class="flex flex-col gap-3">
          <label class="select">
            <span class="select__label">Select a Form:</span>
            <select class="select__control" [formControl]="navToFormControl">
              <option [value]="undefined">Select a Form</option>
              @for (formName of formList; track formName) {
                <option [value]="formName">{{ formName }}</option>
              }
            </select>
          </label>
          <button (click)="navToForm()" [disabled]="!navToFormControl.valid" class="button button--secondary button--sm self-start">Go to form</button>
        </div>
      </div>

      <!-- Test Scenarios Section -->
      <div>
        <h4 class="text-fs-sm h4 mb-4 flex items-center gap-2 border-b pb-2">
          <ng-icon name="lucidePlay" size="1em" class="text-content-secondary"></ng-icon>
          Test Addresses
        </h4>
        <div class="flex flex-wrap gap-3">
          <div class="flex flex-col gap-1">
            <label class="">
              <span class="select__label">State</span>
              <select class="select__control" [(ngModel)]="selectedScenarioState" (ngModelChange)="onStateChange($event)">
                <option value="">-- Select State --</option>
                @for (state of availableScenarioStates; track state) {
                  <option [value]="state">{{ state }}</option>
                }
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class DebugScenariosComponent {
  private quoteDraftStore = inject(QuoteDraftStore);
  private router = inject(Router);
  private userStore = inject(UserStore);
  private quoteFormOptionsStore = inject(QuoteFormOptionsStore);

  readonly availableScenarioStates = ['FL', 'AL', 'AZ', 'CA', 'CO', 'GA', 'LA', 'MS', 'SC', 'TN', 'TX', 'VA'];

  selectedScenarioState = '';

  // Navigation form data
  public formList = [...Array.from(quoteFormMappingMap.keys())];
  public navToFormControl = new FormControl<QuoteFormMappingKey | undefined>(undefined, Validators.required);

  private isInitialized = signal(false);

  // Mock addresses for each state
  private readonly stateAddresses: Record<string, QuoteDraftAddress> = {
    FL: {
      street: '7993 Kaitlin Cir',
      city: 'Lakeland',
      state: 'FL',
      zipcode: '33810',
    },
    AL: {
      street: '1234 Magnolia Ave',
      city: 'Birmingham',
      state: 'AL',
      zipcode: '35203',
    },
    AZ: {
      street: '2830 Louise Ave',
      city: 'Kingman',
      state: 'AZ',
      zipcode: '86401',
    },
    CA: {
      street: '4233 Findlay Way',
      city: 'Livermore',
      state: 'CA',
      zipcode: '94550',
    },
    CO: {
      street: '27 Spruce Way',
      city: 'Telluride',
      state: 'CO',
      zipcode: '81435',
    },
    GA: {
      street: '5678 Peachtree St',
      city: 'Atlanta',
      state: 'GA',
      zipcode: '30309',
    },
    LA: {
      street: '5104 Harding St',
      city: 'Baker',
      state: 'LA',
      zipcode: '70714',
    },
    MS: {
      street: '9876 River Rd',
      city: 'Jackson',
      state: 'MS',
      zipcode: '39201',
    },
    SC: {
      street: '3456 Ocean Blvd',
      city: 'Charleston',
      state: 'SC',
      zipcode: '29401',
    },
    TN: {
      street: '7890 Music Row',
      city: 'Nashville',
      state: 'TN',
      zipcode: '37203',
    },
    TX: {
      street: '1357 Lone Star Dr',
      city: 'Austin',
      state: 'TX',
      zipcode: '78701',
    },
    VA: {
      street: '2468 Colonial Ave',
      city: 'Richmond',
      state: 'VA',
      zipcode: '23220',
    },
  };

  constructor() {
    // Sync with existing state on load
    effect(() => {
      const currentAddress = this.quoteDraftStore.address();
      if (!this.isInitialized() && currentAddress?.state) {
        this.selectedScenarioState = currentAddress.state;
        this.isInitialized.set(true);
      }
    });
  }

  onStateChange(state: string) {
    this.selectedScenarioState = state;
    this.quoteDraftStore.patchStore({ address: this.stateAddresses[state] });
  }

  public navToForm() {
    if (this.navToFormControl.valid) {
      // Load form options before navigation
      this.quoteFormOptionsStore.reset(mockFormOptions);

      // set to quoting to bypass route guard
      this.userStore.startQuoting();
      this.router.navigate([`/quote/${this.navToFormControl.value}`]);
    }
  }
}
